/**
 * Tempo em America/Sao_Paulo (SPEC 1, 7.1, 10.3).
 *
 * Regras:
 * - Instantes trafegam como `Ms` (epoch ms UTC); o dia é a `DateKey` `YYYY-MM-DD` em SP.
 * - `new Date()` / `Date.now()` só existem neste arquivo. Na UI, a hora vem de `useNow()`
 *   (data/hooks.ts), que segue o relógio do provider (simulado na V1).
 * - Nada de moment/dayjs/date-fns: só `Intl` (10.1).
 */
import type { DateKey, Ms } from '@rota/contracts';

export const TZ = 'America/Sao_Paulo' as const;
export const MINUTE = 60_000;
export const HOUR = 3_600_000;
export const DAY = 86_400_000;

/** Relógio real do aparelho. Use `useNow()` na UI; isto é só para o próprio relógio e para o boot. */
export function nowMs(): Ms {
  return Date.now();
}

export interface PartsSP {
  year: number;
  month: number; // 1..12
  day: number;
  hour: number; // 0..23
  minute: number;
  second: number;
  weekday: number; // 0 = domingo … 6 = sábado
}

const partsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  weekday: 'short',
});
const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

let lastPartsMs = Number.NaN;
let lastParts: PartsSP | null = null;

/** Componentes de calendário do instante no fuso de São Paulo. */
export function partsSP(ms: Ms): PartsSP {
  const second = Math.floor(ms / 1000) * 1000;
  if (second === lastPartsMs && lastParts) return lastParts;
  const out: PartsSP = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0, weekday: 0 };
  for (const p of partsFormatter.formatToParts(ms)) {
    switch (p.type) {
      case 'year':
        out.year = Number(p.value);
        break;
      case 'month':
        out.month = Number(p.value);
        break;
      case 'day':
        out.day = Number(p.value);
        break;
      case 'hour':
        out.hour = Number(p.value) % 24;
        break;
      case 'minute':
        out.minute = Number(p.value);
        break;
      case 'second':
        out.second = Number(p.value);
        break;
      case 'weekday':
        out.weekday = WEEKDAYS[p.value] ?? 0;
        break;
      default:
        break;
    }
  }
  lastPartsMs = second;
  lastParts = out;
  return out;
}

/** Deslocamento do fuso (local − UTC) em ms no instante dado. SP: −3 h desde 2019. */
export function offsetSP(ms: Ms): number {
  const p = partsSP(ms);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

/** `YYYY-MM-DD` do instante em São Paulo. */
export function toDateKeySP(ms: Ms): DateKey {
  const p = partsSP(ms);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** Hora (0–23) do instante em São Paulo. */
export function hourSP(ms: Ms): number {
  return partsSP(ms).hour;
}

/** Minuto do dia (0–1439) em São Paulo. */
export function minuteOfDaySP(ms: Ms): number {
  const p = partsSP(ms);
  return p.hour * 60 + p.minute;
}

/** Dia da semana (0 = domingo) em São Paulo. */
export function weekdaySP(ms: Ms): number {
  return partsSP(ms).weekday;
}

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isDateKey(value: unknown): value is DateKey {
  if (typeof value !== 'string') return false;
  const m = DATE_KEY_RE.exec(value);
  if (!m) return false;
  const month = Number(m[2]);
  const day = Number(m[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(Number(m[1]), month);
}

function parseKey(key: DateKey): [number, number, number] {
  const m = DATE_KEY_RE.exec(key);
  if (!m) throw new RangeError(`DateKey inválida: ${key}`);
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** Dias no mês (mês 1..12). */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Converte um horário de parede em SP para `Ms` (resolve o fuso mesmo em transições). */
function wallTimeToMs(year: number, month: number, day: number, hour = 0, minute = 0): Ms {
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  let guess = asUtc - offsetSP(asUtc);
  const check = offsetSP(guess);
  if (asUtc - check !== guess) guess = asUtc - check;
  return guess;
}

/** 00:00 em São Paulo do dia do instante (ou da `DateKey`). */
export function startOfDaySP(at: Ms | DateKey): Ms {
  if (typeof at === 'string') {
    const [y, m, d] = parseKey(at);
    return wallTimeToMs(y, m, d);
  }
  const p = partsSP(at);
  return wallTimeToMs(p.year, p.month, p.day);
}

/** Último ms do dia em São Paulo (23:59:59.999). */
export function endOfDaySP(at: Ms | DateKey): Ms {
  const key = typeof at === 'string' ? at : toDateKeySP(at);
  return startOfDaySP(addDaysKey(key, 1)) - 1;
}

/** Soma `n` dias de calendário a uma `DateKey`. */
export function addDaysKey(key: DateKey, n: number): DateKey {
  const [y, m, d] = parseKey(key);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/** Diferença em dias de calendário (`b − a`). */
export function diffDaysKey(a: DateKey, b: DateKey): number {
  const [ya, ma, da] = parseKey(a);
  const [yb, mb, db] = parseKey(b);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / DAY);
}

/** Dia da semana de uma `DateKey` (0 = domingo). */
export function weekdayOfKey(key: DateKey): number {
  const [y, m, d] = parseKey(key);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Primeiro dia do mês da `DateKey`. */
export function startOfMonthKey(key: DateKey): DateKey {
  return `${key.slice(0, 7)}-01`;
}

/** Último dia do mês da `DateKey`. */
export function endOfMonthKey(key: DateKey): DateKey {
  const [y, m] = parseKey(key);
  return `${y}-${pad2(m)}-${pad2(daysInMonth(y, m))}`;
}

/** Lista inclusiva de `DateKey` entre `from` e `to`. */
export function eachDayKey(from: DateKey, to: DateKey): DateKey[] {
  const n = diffDaysKey(from, to);
  const out: DateKey[] = [];
  for (let i = 0; i <= n; i++) out.push(addDaysKey(from, i));
  return out;
}

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** "14:32" → minutos do dia (872). Inválido → null. */
export function parseHHMM(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = HHMM_RE.exec(value);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Minutos do dia → "HH:MM". */
export function minutesToHHMM(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

/** Instante → "HH:MM" em São Paulo. */
export function toHHMM(ms: Ms): string {
  const p = partsSP(ms);
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** `DateKey` + "HH:MM" (ou minutos do dia) → `Ms`. */
export function atTimeSP(key: DateKey, time: string | number): Ms {
  const minutes = typeof time === 'number' ? time : parseHHMM(time);
  if (minutes === null) throw new RangeError(`Horário inválido: ${String(time)}`);
  const [y, m, d] = parseKey(key);
  return wallTimeToMs(y, m, d, Math.floor(minutes / 60), minutes % 60);
}

/** Fração do dia decorrida (0..1) em São Paulo. */
export function dayFractionSP(ms: Ms): number {
  const start = startOfDaySP(ms);
  const end = endOfDaySP(ms) + 1;
  return Math.min(1, Math.max(0, (ms - start) / (end - start)));
}

export function isSameDaySP(a: Ms, b: Ms): boolean {
  return toDateKeySP(a) === toDateKeySP(b);
}

/** Arredonda para baixo ao passo de `stepMin` minutos (ex.: Replay em passos de 5 min). */
export function floorToStep(ms: Ms, stepMin: number): Ms {
  const step = stepMin * MINUTE;
  const start = startOfDaySP(ms);
  return start + Math.floor((ms - start) / step) * step;
}

/**
 * Tempo relativo curto, em pt-BR: "agora", "há 3 min", "há 1 h 30 min", "há 5 h", "há 3 dias",
 * "em 5 min".
 * Não é `aria-live` (10.7): o leitor só lê ao focar.
 */
export function relativeTime(ms: Ms, now: Ms): string {
  const diff = now - ms;
  const abs = Math.abs(diff);
  if (abs < 45_000) return 'agora';
  const future = diff < 0;
  let text: string;
  const minutes = Math.max(1, Math.round(abs / MINUTE));
  if (minutes < 60) text = `${minutes} min`;
  else if (abs < 3 * HOUR) {
    // Entre 1 h e 3 h, horas + minutos em passos de 5 min ("há 1 h 30 min"): arredondar só a
    // hora fazia o mesmo silêncio de 1 h 30 min virar "há 1 h" num lugar e "há 2 h" no outro.
    const step = Math.round(abs / (5 * MINUTE)) * 5;
    const h = Math.floor(step / 60);
    const m = step % 60;
    text = m === 0 ? `${h} h` : `${h} h ${m} min`;
  } else if (abs < DAY) text = `${Math.round(abs / HOUR)} h`;
  else {
    const days = Math.round(abs / DAY);
    text = days === 1 ? '1 dia' : `${days} dias`;
  }
  return future ? `em ${text}` : `há ${text}`;
}

/** Duração em minutos inteiros entre dois instantes (para "faltam 28 min"). */
export function minutesBetween(from: Ms, to: Ms): number {
  return Math.round((to - from) / MINUTE);
}
